import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  BookOpen,
  CheckCircle2,
  XCircle,
  BellOff,
  ChevronUp,
  Calendar,
  AlertCircle,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  MinusCircle,
  ArrowRight,
  MessageSquare,
  MoreVertical,
  Check,
  Lightbulb,
} from 'lucide-react'

const STATUS_LABELS = {
  GENERATED: 'New',
  PRESENTED: 'Active',
  ACCEPTED: 'Accepted',
  DISMISSED: 'Dismissed',
  SNOOZED: 'Snoozed',
  COMPLETED: 'Completed',
  EXPIRED: 'Expired',
}

const STATUS_COLORS = {
  GENERATED: 'text-sky-400',
  PRESENTED: 'text-cinema-glow',
  ACCEPTED: 'text-emerald-400',
  DISMISSED: 'text-cinema-muted',
  SNOOZED: 'text-amber-400',
  COMPLETED: 'text-emerald-400',
  EXPIRED: 'text-cinema-muted',
}

const PRIORITY_LABELS = {
  REQUIRED_NEXT: 'Next up',
  HIGH_VALUE: 'High priority',
  RECOMMENDED: 'Recommended',
  OPTIONAL: 'Optional',
}

/**
 * Typographic meta line — priority, type and status as quiet text,
 * separated by middle dots instead of pill badges.
 */
function RecommendationMeta({ rec }) {
  const status = STATUS_LABELS[rec.status] || 'New'
  const statusColor = STATUS_COLORS[rec.status] || 'text-cinema-muted'
  const priority = PRIORITY_LABELS[rec.priority]
  const isLead = rec.priority === 'REQUIRED_NEXT'
  return (
    <p className="font-sans text-[11px] uppercase tracking-wider mb-1.5">
      {priority && (
        <span className={isLead ? 'text-cinema-glow font-medium' : 'text-cinema-muted'}>
          {priority}
        </span>
      )}
      {priority && <span className="text-cinema-muted/40"> &middot; </span>}
      <span className="text-cinema-muted">{rec.recommendation_type.replace(/_/g, ' ')}</span>
      <span className="text-cinema-muted/40"> &middot; </span>
      <span className={statusColor}>{status}</span>
    </p>
  )
}

function RecommendationRow({ rec, onAction, onFeedback }) {
  const [showWhy, setShowWhy] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const [showMoreActions, setShowMoreActions] = useState(false)
  const [actionLoading, setActionLoading] = useState(null)
  const navigate = useNavigate()

  const isActive = ['GENERATED', 'PRESENTED', 'ACCEPTED', 'SNOOZED'].includes(rec.status)

  const handleActionClick = async (action) => {
    setActionLoading(action)
    try {
      await onAction(rec.id, action)
    } finally {
      setActionLoading(null)
      setShowMoreActions(false)
    }
  }

  const handleAskTutor = () => {
    // Navigate to tutor with context
    const query = encodeURIComponent(`I'm working on recommendation: "${rec.title}". ${rec.reason || ''}`)
    navigate(`/student/tutor?prompt=${query}`)
  }

  return (
    <div className="py-6 first:pt-0">
      <div className="min-w-0">
        <RecommendationMeta rec={rec} />

        {/* What should I do */}
        <h4 className="font-display text-base sm:text-lg text-white font-light leading-snug">
          {rec.title}
        </h4>
        <p className="font-sans text-xs sm:text-sm text-cinema-muted leading-relaxed mt-1.5 max-w-3xl">
          {rec.description || rec.reason}
        </p>

        {/* Why — expandable explanation */}
        {showWhy && (
          <div className="mt-4 pl-4 border-l-2 border-cinema-glow/30 space-y-2 animate-fade-in max-w-3xl">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-cinema-gold">Why this was suggested</p>
              <button
                type="button"
                onClick={() => setShowWhy(false)}
                className="text-cinema-muted hover:text-white p-1"
                aria-label="Close explanation"
              >
                <ChevronUp size={14} />
              </button>
            </div>
            <p className="text-xs sm:text-sm text-cinema-soft leading-relaxed">{rec.reason}</p>

            {rec.evidence && Array.isArray(rec.evidence) && rec.evidence.length > 0 && (
              <div className="pt-2 border-t border-white/5 space-y-1.5">
                <p className="text-[11px] uppercase tracking-wider text-cinema-muted font-medium">
                  Evidence & signals
                </p>
                {rec.evidence.map((ev, i) => (
                  <div key={i} className="text-xs text-cinema-soft/80 pl-3 border-l border-cinema-glow/30 py-0.5">
                    <span className="text-cinema-glow/70 font-mono text-[10px]">[{ev.source}]</span>{' '}
                    {ev.detail}
                  </div>
                ))}
              </div>
            )}

            <p className="text-[11px] text-cinema-muted/60 pt-1">
              Advisory suggestion. You are in complete control of your pace and path.
            </p>
          </div>
        )}

        {/* Feedback — expandable */}
        {showFeedback && (
          <div className="mt-4 space-y-2.5 animate-fade-in max-w-2xl">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-cinema-gold">How was this recommendation?</p>
              <button
                type="button"
                onClick={() => setShowFeedback(false)}
                className="text-cinema-muted hover:text-white p-1"
                aria-label="Close feedback"
              >
                <ChevronUp size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { type: 'HELPFUL', label: 'Helpful', icon: ThumbsUp, color: 'text-emerald-400' },
                { type: 'NOT_HELPFUL', label: 'Not helpful', icon: ThumbsDown, color: 'text-red-400' },
                { type: 'IRRELEVANT', label: 'Irrelevant', icon: MinusCircle, color: 'text-cinema-muted' },
                { type: 'TOO_DIFFICULT', label: 'Too difficult', icon: AlertCircle, color: 'text-amber-400' },
                { type: 'TOO_EASY', label: 'Too easy', icon: CheckCircle2, color: 'text-sky-400' },
                { type: 'ALREADY_KNEW', label: 'Already knew', icon: CheckCircle2, color: 'text-cinema-gold' },
              ].map(({ type, label, icon: FbIcon, color }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    onFeedback(rec.id, type)
                    setShowFeedback(false)
                  }}
                  className={`min-h-[42px] flex items-center justify-center gap-1.5 px-2.5 py-2 text-xs border border-white/10 hover:border-white/25 hover:bg-white/[0.03] transition-colors ${color}`}
                >
                  <FbIcon size={13} className="flex-shrink-0" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {isActive && (
          <div className="mt-4 pt-4 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                disabled={actionLoading !== null}
                onClick={() => handleActionClick('accept')}
                className="flex-1 sm:flex-initial min-h-[40px] px-4 py-2 text-xs font-medium border border-cinema-glow/40 text-cinema-glow hover:bg-cinema-glow/10 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <CheckCircle2 size={14} />
                {actionLoading === 'accept' ? 'Accepting...' : 'Accept'}
              </button>

              <button
                type="button"
                disabled={actionLoading !== null}
                onClick={() => handleActionClick('complete')}
                className="flex-1 sm:flex-initial min-h-[40px] px-4 py-2 text-xs font-medium border border-white/15 text-cinema-soft hover:text-white hover:border-white/30 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <Check size={14} />
                {actionLoading === 'complete' ? 'Saving...' : 'Done'}
              </button>
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowWhy(!showWhy)}
                className={`text-xs transition-colors ${
                  showWhy ? 'text-cinema-glow' : 'text-cinema-muted hover:text-white'
                }`}
              >
                Why?
              </button>

              <button
                type="button"
                onClick={() => setShowFeedback(!showFeedback)}
                className={`text-xs transition-colors ${
                  showFeedback ? 'text-cinema-gold' : 'text-cinema-muted hover:text-white'
                }`}
              >
                Feedback
              </button>

              <button
                type="button"
                onClick={handleAskTutor}
                className="text-xs text-cinema-muted hover:text-cinema-glow transition-colors flex items-center gap-1"
              >
                <MessageSquare size={12} />
                Ask Tutor
              </button>

              {/* More actions dropdown (Snooze / Dismiss) */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowMoreActions(!showMoreActions)}
                  className="min-h-[40px] w-[40px] border border-white/10 hover:border-white/25 text-cinema-muted hover:text-white transition-colors flex items-center justify-center"
                  aria-label="More actions"
                  aria-expanded={showMoreActions}
                >
                  <MoreVertical size={14} />
                </button>

                {showMoreActions && (
                  <div className="absolute right-0 bottom-full mb-1 sm:bottom-auto sm:top-full sm:mt-1 z-20 w-44 cinema-glass-strong border border-white/10 p-1 shadow-2xl animate-fade-in">
                    <button
                      type="button"
                      onClick={() => handleActionClick('snooze')}
                      className="w-full min-h-[40px] px-3 py-2 text-left text-xs text-amber-400 hover:bg-white/5 flex items-center gap-2 transition-colors"
                    >
                      <BellOff size={14} />
                      <span>Snooze for later</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleActionClick('dismiss')}
                      className="w-full min-h-[40px] px-3 py-2 text-left text-xs text-red-400 hover:bg-white/5 flex items-center gap-2 transition-colors"
                    >
                      <XCircle size={14} />
                      <span>Dismiss recommendation</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function AIPersonalizationPage() {
  const [recommendations, setRecommendations] = useState([])
  const [context, setContext] = useState(null)
  const [preferences, setPreferences] = useState(null)
  const [studyPlan, setStudyPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('recommendations')
  const [generating, setGenerating] = useState(false)
  const [generatingPlan, setGeneratingPlan] = useState(false)
  const [planParams, setPlanParams] = useState({ dailyHours: 2, targetDate: '' })
  const [recFilter, setRecFilter] = useState('all')

  useEffect(() => {
    document.title = 'AI Learning | SMA Student Portal'
  }, [])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [recsRes, ctxRes, prefsRes] = await Promise.all([
        api.get('/tutor/my-recommendations').catch(() => ({ data: { recommendations: [] } })),
        api.get('/tutor/my-context').catch(() => ({ data: { context: null } })),
        api.get('/tutor/my-preferences').catch(() => ({ data: { preferences: {}, catalog: [] } })),
      ])
      setRecommendations(recsRes.data?.recommendations || [])
      setContext(ctxRes.data?.context || null)
      setPreferences(prefsRes.data || { preferences: {}, catalog: [] })
    } catch (err) {
      console.error('Personalization load error:', err)
      toast.error('Could not load all personalized learning data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleAction = async (recId, action) => {
    try {
      await api.post(`/tutor/my-recommendations/${recId}/${action}`)
      toast.success(`Recommendation ${action}ed`)
      loadData()
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action} recommendation`)
    }
  }

  const handleFeedback = async (recId, feedbackType) => {
    try {
      await api.post(`/tutor/my-recommendations/${recId}/feedback`, { feedbackType })
      toast.success('Thank you for your feedback!')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit feedback')
    }
  }

  const handleGenerate = async () => {
    try {
      setGenerating(true)
      const res = await api.post('/tutor/my-recommendations/generate', {
        courseId: context?.course?.id,
      })
      setRecommendations(res.data?.recommendations || [])
      toast.success(`Generated ${res.data?.count || 0} fresh recommendations`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate recommendations')
    } finally {
      setGenerating(false)
    }
  }

  const handleGeneratePlan = async () => {
    try {
      setGeneratingPlan(true)
      const res = await api.post('/tutor/my-study-plan', {
        courseId: context?.course?.id,
        dailyHours: parseFloat(planParams.dailyHours) || 2,
        targetDate: planParams.targetDate || null,
      })
      setStudyPlan(res.data?.plan || null)
      toast.success('Study plan generated successfully!')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate study plan')
    } finally {
      setGeneratingPlan(false)
    }
  }

  const handleSavePreference = async (key, value) => {
    try {
      await api.patch('/tutor/my-preferences', { key, value })
      toast.success('Preference updated')
      loadData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save preference')
    }
  }

  // Filter recommendations
  const filteredRecommendations = useMemo(() => {
    if (recFilter === 'next') {
      return recommendations.filter((r) => r.priority === 'REQUIRED_NEXT' || r.priority === 'HIGH_VALUE')
    }
    if (recFilter === 'active') {
      return recommendations.filter((r) => ['GENERATED', 'PRESENTED'].includes(r.status))
    }
    if (recFilter === 'completed') {
      return recommendations.filter((r) => ['COMPLETED', 'ACCEPTED'].includes(r.status))
    }
    return recommendations
  }, [recommendations, recFilter])

  const activeRecCount = useMemo(() => {
    return recommendations.filter((r) => ['GENERATED', 'PRESENTED', 'ACCEPTED', 'SNOOZED'].includes(r.status)).length
  }, [recommendations])

  const tabs = [
    { id: 'recommendations', label: 'Recommendations', badge: activeRecCount },
    { id: 'progress', label: 'My Progress' },
    { id: 'studyplan', label: 'Study Plan' },
    { id: 'preferences', label: 'Preferences' },
  ]

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-3">
        <RefreshCw className="w-8 h-8 text-cinema-glow animate-spin" />
        <p className="font-sans text-sm text-cinema-muted">Loading your personalized learning experience...</p>
      </div>
    )
  }

  return (
    <div className="space-y-10 max-w-[1400px] pb-12">
      {/* ── Page header ── */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light tracking-tight">
            AI Learning
          </h1>
          <p className="font-sans text-sm text-cinema-muted mt-2 max-w-2xl leading-relaxed">
            Personalized guidance based on your course, progress and current learning.
          </p>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="text-xs sm:text-sm text-cinema-muted hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw size={13} className={generating ? 'animate-spin' : ''} />
            {generating ? 'Analyzing…' : 'Refresh'}
          </button>
          <Link
            to="/student/tutor"
            className="cinema-btn-primary !min-h-0 !px-5 !py-2.5 text-xs sm:text-sm"
          >
            <MessageSquare size={15} />
            Chat with AI Tutor
          </Link>
        </div>
      </div>

      {/* ── Learning context band ── */}
      {context && (
        <section>
          <div className="cinema-section-header">
            <h2 className="cinema-section-title">Learning journey</h2>
            {context.course?.course_code && (
              <span className="text-xs font-mono text-cinema-muted">
                {context.course.course_code}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:divide-x lg:divide-white/5">
            <div className="cinema-stat lg:pr-8">
              <span className="cinema-stat-label">Enrolled course</span>
              <span className="font-display text-base sm:text-lg text-white font-light leading-snug">
                {context.course?.title || 'No active course'}
              </span>
            </div>

            <div className="cinema-stat lg:px-8">
              <span className="cinema-stat-label">Progress</span>
              <span className="font-display text-base sm:text-lg text-white font-light">
                {context.progress?.percentage || 0}%
              </span>
              <div className="w-full h-px bg-white/10">
                <div
                  className="bg-cinema-glow h-px transition-all duration-500"
                  style={{ width: `${context.progress?.percentage || 0}%` }}
                />
              </div>
              <span className="cinema-stat-sub">
                {context.progress?.completed_lessons || 0} of {context.progress?.total_lessons || 0} lessons
              </span>
            </div>

            <div className="cinema-stat lg:px-8">
              <span className="cinema-stat-label">Current focus</span>
              <span className="font-display text-base sm:text-lg text-white font-light leading-snug" title={context.current_lesson?.title}>
                {context.current_lesson?.title || 'Up to date'}
              </span>
              {context.course?.id && (
                <Link
                  to={`/student/courses/${context.course.id}`}
                  className="inline-flex items-center gap-1 text-xs text-cinema-glow hover:text-white transition-colors"
                >
                  Go to course <ArrowRight size={11} />
                </Link>
              )}
            </div>

            <div className="cinema-stat lg:pl-8">
              <span className="cinema-stat-label">Performance</span>
              <span className="font-display text-base sm:text-lg text-white font-light">
                {context.performance?.average_score !== null && context.performance?.average_score !== undefined
                  ? `${context.performance.average_score}%`
                  : '—'}
              </span>
              <span className="cinema-stat-sub">
                {context.performance?.total_graded
                  ? `${context.performance.total_graded} graded`
                  : 'No graded items'}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* ── Primary navigation ── */}
      <nav className="border-b border-white/5" aria-label="AI learning sections">
        <div className="flex items-center gap-6 sm:gap-8 overflow-x-auto no-scrollbar">
          {tabs.map(({ id, label, badge }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              aria-current={activeTab === id ? 'page' : undefined}
              className={`pb-3 -mb-px text-sm whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
                activeTab === id
                  ? 'text-white border-cinema-glow'
                  : 'text-cinema-muted border-transparent hover:text-white'
              }`}
            >
              {label}
              {badge !== undefined && badge > 0 && (
                <span className="ml-1.5 text-cinema-muted font-mono text-xs">({badge})</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* ── Recommendations ── */}
      {activeTab === 'recommendations' && (
        <div>
          {/* Filters */}
          <div className="flex items-center justify-between gap-4 pb-5">
            <div className="flex items-center gap-5 overflow-x-auto no-scrollbar">
              {[
                { id: 'all', label: 'All', count: recommendations.length },
                { id: 'next', label: 'Next up', count: recommendations.filter((r) => r.priority === 'REQUIRED_NEXT' || r.priority === 'HIGH_VALUE').length },
                { id: 'active', label: 'Active', count: activeRecCount },
                { id: 'completed', label: 'History', count: recommendations.filter((r) => ['COMPLETED', 'ACCEPTED', 'DISMISSED'].includes(r.status)).length },
              ].map(({ id, label, count }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setRecFilter(id)}
                  className={`pb-1 text-xs whitespace-nowrap border-b transition-colors flex-shrink-0 ${
                    recFilter === id
                      ? 'text-white border-cinema-glow'
                      : 'text-cinema-muted border-transparent hover:text-white'
                  }`}
                >
                  {label} <span className="text-cinema-muted/70 font-mono">{count}</span>
                </button>
              ))}
            </div>

            <span className="text-xs text-cinema-muted hidden sm:inline shrink-0">
              {filteredRecommendations.length} shown
            </span>
          </div>

          {/* Recommendation list */}
          {filteredRecommendations.length === 0 ? (
            <div className="cinema-empty border-t border-white/5">
              <Lightbulb size={32} className="mx-auto mb-4 text-cinema-muted/20" />
              <h3 className="font-display text-lg text-white font-light mb-2">No recommendations</h3>
              <p className="font-sans text-sm text-cinema-muted max-w-md mx-auto">
                {recFilter !== 'all'
                  ? 'Nothing under this filter. Switch back to "All" to view your full history.'
                  : 'Your recommendation queue is empty. Run a fresh analysis based on your recent activity.'}
              </p>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="cinema-btn-text mt-4 inline-flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw size={13} className={generating ? 'animate-spin' : ''} />
                Generate recommendations
              </button>
            </div>
          ) : (
            <div className="divide-y divide-white/5 border-t border-white/5">
              {filteredRecommendations.map((rec) => (
                <RecommendationRow
                  key={rec.id}
                  rec={rec}
                  onAction={handleAction}
                  onFeedback={handleFeedback}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── My Progress ── */}
      {activeTab === 'progress' && (
        <div className="space-y-10">
          {context ? (
            <>
              <div className="grid grid-cols-3 gap-6 sm:gap-10">
                <div className="cinema-stat">
                  <span className="cinema-stat-value">{context.progress?.completed_lessons || 0}</span>
                  <span className="cinema-stat-label">Completed</span>
                </div>
                <div className="cinema-stat">
                  <span className="cinema-stat-value">{context.progress?.in_progress_lessons || 0}</span>
                  <span className="cinema-stat-label">In progress</span>
                </div>
                <div className="cinema-stat">
                  <span className="cinema-stat-value">{context.progress?.total_lessons || 0}</span>
                  <span className="cinema-stat-label">Total lessons</span>
                </div>
              </div>

              {/* Assignments */}
              {context.assignments && context.assignments.length > 0 && (
                <section>
                  <div className="cinema-section-header">
                    <h3 className="cinema-section-title">Assignments & practical checks</h3>
                    <span className="text-xs text-cinema-muted font-mono">
                      {context.assignments.length} total
                    </span>
                  </div>

                  <div className="cinema-list border-t border-white/5">
                    {context.assignments.slice(0, 8).map((a) => (
                      <div
                        key={a.id}
                        className="py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-white font-light truncate">{a.title}</p>
                          <p className="text-xs text-cinema-muted truncate">{a.lesson_title || 'General'}</p>
                        </div>
                        <div className="flex-shrink-0">
                          {a.score !== null && a.score !== undefined ? (
                            <span
                              className={`text-xs font-mono ${
                                a.score < (a.max_score || 100) * 0.6
                                  ? 'text-red-400'
                                  : 'text-emerald-400'
                              }`}
                            >
                              {a.score}/{a.max_score}
                            </span>
                          ) : a.submission_status ? (
                            <span className="text-xs text-amber-400">Submitted · pending review</span>
                          ) : (
                            <span className="text-xs text-cinema-muted">Pending submission</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Topic mastery */}
              {context.learning_topics && context.learning_topics.length > 0 && (
                <section>
                  <div className="cinema-section-header">
                    <h3 className="cinema-section-title">Topic mastery</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 border-t border-white/5">
                    {context.learning_topics.slice(0, 10).map((t, i) => (
                      <div
                        key={i}
                        className="py-3 border-b border-white/5 flex items-center justify-between gap-3"
                      >
                        <p className="text-sm text-white font-light truncate">{t.topic}</p>
                        <span
                          className={`text-xs capitalize flex-shrink-0 ${
                            t.status === 'strong'
                              ? 'text-emerald-400'
                              : t.status === 'competent'
                              ? 'text-cinema-glow'
                              : t.status === 'developing'
                              ? 'text-amber-400'
                              : t.status === 'needs_review'
                              ? 'text-red-400'
                              : 'text-cinema-muted'
                          }`}
                        >
                          {t.status?.replace(/_/g, ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <div className="cinema-empty">
              <p className="font-sans text-sm text-cinema-muted">No course progress data is available yet.</p>
            </div>
          )}
        </div>
      )}

      {/* ── Study Plan ── */}
      {activeTab === 'studyplan' && (
        <div className="space-y-10">
          {/* Plan generator */}
          <section>
            <div className="cinema-section-header">
              <div>
                <h3 className="cinema-section-title">Generate a study plan</h3>
                <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-1.5 leading-relaxed max-w-xl">
                  Distributes your remaining lessons across the days and hours you have available.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
              <div className="space-y-1.5">
                <label htmlFor="daily-hours" className="text-xs text-cinema-muted block">
                  Daily study target (hours)
                </label>
                <input
                  id="daily-hours"
                  type="number"
                  min="0.5"
                  max="8"
                  step="0.5"
                  value={planParams.dailyHours}
                  onChange={(e) => setPlanParams({ ...planParams, dailyHours: e.target.value })}
                  className="w-full min-h-[44px] px-3 py-2.5 bg-transparent border border-white/10 text-white font-mono text-sm focus:border-cinema-glow focus:outline-none transition-colors"
                />
                <div className="flex items-center gap-1.5 pt-1">
                  {[1, 1.5, 2, 3, 4].map((hrs) => (
                    <button
                      key={hrs}
                      type="button"
                      onClick={() => setPlanParams({ ...planParams, dailyHours: hrs })}
                      className={`px-2.5 py-1 text-[11px] font-mono border transition-colors ${
                        Number(planParams.dailyHours) === hrs
                          ? 'border-cinema-glow/50 text-cinema-glow'
                          : 'border-white/10 text-cinema-muted hover:text-white'
                      }`}
                    >
                      {hrs}h
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="target-date" className="text-xs text-cinema-muted block">
                  Target finish date <span className="text-cinema-muted/50">(optional)</span>
                </label>
                <input
                  id="target-date"
                  type="date"
                  value={planParams.targetDate}
                  onChange={(e) => setPlanParams({ ...planParams, targetDate: e.target.value })}
                  className="w-full min-h-[44px] px-3 py-2.5 bg-transparent border border-white/10 text-white text-sm focus:border-cinema-glow focus:outline-none transition-colors"
                />
                <p className="text-[11px] text-cinema-muted/60">Leave blank to auto-calculate by pace.</p>
              </div>

              <div className="flex items-end sm:col-span-2 lg:col-span-1">
                <button
                  type="button"
                  onClick={handleGeneratePlan}
                  disabled={generatingPlan}
                  className="cinema-btn-primary !min-h-0 !px-5 !py-2.5 text-xs sm:text-sm w-full disabled:opacity-50"
                >
                  <Calendar size={15} />
                  {generatingPlan ? 'Calculating…' : 'Generate plan'}
                </button>
              </div>
            </div>
          </section>

          {/* Generated plan */}
          {studyPlan && (
            <section className="animate-fade-in">
              <div className="cinema-section-header">
                <h3 className="cinema-section-title">Your study plan</h3>
                <span className="text-xs font-mono text-cinema-gold">
                  {studyPlan.summary?.daily_hours || 2}h / day
                </span>
              </div>

              {studyPlan.status === 'COURSE_COMPLETE' ? (
                <div className="pl-4 border-l-2 border-emerald-500/50 space-y-1.5">
                  <h4 className="text-white font-display font-light text-base flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-400" /> Course completed
                  </h4>
                  <p className="text-sm text-cinema-soft">{studyPlan.message}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 mb-8">
                    <div className="cinema-stat">
                      <span className="cinema-stat-value">{studyPlan.summary?.remaining_lessons || 0}</span>
                      <span className="cinema-stat-label">Lessons left</span>
                    </div>
                    <div className="cinema-stat">
                      <span className="cinema-stat-value">{studyPlan.summary?.daily_hours || 0}h</span>
                      <span className="cinema-stat-label">Daily hours</span>
                    </div>
                    <div className="cinema-stat">
                      <span className="cinema-stat-value">{studyPlan.summary?.estimated_days || 0}</span>
                      <span className="cinema-stat-label">Est. days</span>
                    </div>
                    <div className="cinema-stat">
                      <span className="cinema-stat-value">{studyPlan.summary?.estimated_weeks || 0}</span>
                      <span className="cinema-stat-label">Est. weeks</span>
                    </div>
                  </div>

                  {studyPlan.safety_warnings?.length > 0 && (
                    <div className="pl-4 border-l-2 border-amber-500/40 space-y-1 mb-8">
                      {studyPlan.safety_warnings.map((w, i) => (
                        <p key={i} className="text-xs text-amber-300 flex items-start gap-1.5">
                          <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                          <span>{w}</span>
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Daily schedule */}
                  <div className="border-t border-white/5">
                    <div className="cinema-list max-h-[480px] overflow-y-auto pr-1 no-scrollbar">
                      {studyPlan.schedule?.map((day) => (
                        <div
                          key={day.day}
                          className="py-4 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6"
                        >
                          <div className="flex sm:flex-col items-baseline sm:items-start gap-2 sm:gap-0.5 flex-shrink-0 sm:w-32">
                            <span className="text-xs font-mono text-cinema-glow">
                              Day {day.day}
                            </span>
                            <span className="text-xs text-cinema-muted">{day.date}</span>
                          </div>

                          <div className="flex-1 space-y-1 min-w-0">
                            {day.lessons.map((l, i) => (
                              <div key={i} className="text-xs text-white font-light flex items-center gap-1.5">
                                <BookOpen size={11} className="text-cinema-muted flex-shrink-0" />
                                <span>{l.title}</span>
                                {l.module && (
                                  <span className="text-cinema-muted">
                                    ({l.module})
                                  </span>
                                )}
                              </div>
                            ))}

                            {day.assignments_due?.length > 0 && (
                              <div className="mt-1.5 pt-1.5 border-t border-white/5">
                                <span className="text-[11px] text-amber-400 flex items-center gap-1">
                                  <AlertCircle size={11} />
                                  Due: {day.assignments_due.map((a) => a.title).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>

                          <span className="text-xs font-mono text-cinema-muted flex-shrink-0 self-start">
                            {day.estimated_hours}h
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {studyPlan.safety_note && (
                    <p className="text-[11px] text-cinema-muted/60 pt-4 mt-2 border-t border-white/5 italic">
                      {studyPlan.safety_note}
                    </p>
                  )}
                </>
              )}
            </section>
          )}
        </div>
      )}

      {/* ── Preferences ── */}
      {activeTab === 'preferences' && preferences && (
        <section>
          <div className="cinema-section-header">
            <div>
              <h3 className="cinema-section-title">Learning preferences</h3>
              <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-1.5 leading-relaxed max-w-xl">
                Tailor how recommendations handle pacing and assistance. Changes apply instantly.
              </p>
            </div>
          </div>

          <div className="divide-y divide-white/5 border-t border-white/5">
            {(preferences.catalog || []).map((pref) => {
              const current = preferences.preferences?.[pref.key]
              const activeVal = current?.value?.value || current?.value
              return (
                <div key={pref.key} className="py-5 space-y-3">
                  <div>
                    <p className="text-sm text-white font-light">{pref.label}</p>
                    {pref.description && (
                      <p className="text-xs text-cinema-muted mt-0.5">{pref.description}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {pref.options.map((opt) => {
                      const isSelected = activeVal === opt
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => handleSavePreference(pref.key, opt)}
                          aria-pressed={isSelected}
                          className={`min-h-[40px] px-4 py-2 text-xs border transition-colors flex items-center gap-2 ${
                            isSelected
                              ? 'border-cinema-glow/50 text-cinema-glow'
                              : 'border-white/10 text-cinema-muted hover:text-white hover:border-white/25'
                          }`}
                        >
                          {isSelected && <Check size={13} />}
                          <span className="capitalize">{opt.replace(/_/g, ' ')}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}
